import { morphSection } from '@theme/section-renderer';

/**
 * Removes "checkout-only" products from the cart when a shopper returns to the
 * storefront.
 *
 * Some products (e.g. gift wrapping) are auto-added by a checkout extension and
 * live on the shared cart object, so they persist onto the storefront cart when
 * the shopper navigates back. This module strips any cart line whose product is
 * listed in the `cart_strip_products` theme setting.
 *
 * The product ids are rendered by `snippets/scripts.liquid` into a JSON
 * `<script id="cart-strip-products-data">` tag, and this module is only loaded
 * when that setting is non-empty — so it adds zero overhead by default.
 */

/** @type {number[]} */
let stripIds = [];

const dataEl = document.getElementById('cart-strip-products-data');
if (dataEl?.textContent) {
  try {
    const parsed = JSON.parse(dataEl.textContent);
    if (Array.isArray(parsed)) stripIds = parsed.filter((id) => typeof id === 'number' && Number.isFinite(id));
  } catch (error) {
    console.warn('[cart-strip-products] Could not parse product id list:', error);
  }
}

const routes = /** @type {{ cart_url?: string, cart_update_url?: string }} */ (Theme?.routes ?? {});
const cartUrl = routes.cart_url || '/cart';
const cartUpdateUrl = routes.cart_update_url || '/cart/update';

/**
 * Reads the cart and removes any line whose product is in the strip list, then
 * refreshes the cart drawer / cart page and the header count bubble in place.
 * @returns {Promise<void>}
 */
async function stripCheckoutOnlyProducts() {
  if (stripIds.length === 0) return;

  const targets = new Set(stripIds);

  let cart;
  try {
    const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Cart read failed with status ${response.status}`);
    cart = await response.json();
  } catch (error) {
    console.warn('[cart-strip-products] Could not read cart:', error);
    return;
  }

  /** @type {Record<string, number>} */
  const updates = {};
  /** @type {string[]} */
  const removedKeys = [];

  for (const item of cart?.items ?? []) {
    if (targets.has(item.product_id)) {
      updates[item.key] = 0;
      removedKeys.push(item.key);
    }
  }

  if (removedKeys.length === 0) return;

  // Collect the cart section components once — used both to request their
  // re-rendered HTML and to morph them below.
  const cartComponents = /** @type {HTMLElement[]} */ ([
    ...document.querySelectorAll('cart-items-component[data-section-id]'),
  ]);
  /** @type {Set<string>} */
  const sectionIds = new Set();
  for (const el of cartComponents) {
    const id = el.getAttribute('data-section-id');
    if (id) sectionIds.add(id);
  }

  /** @type {Record<string, unknown>} */
  const body = { updates };
  if (sectionIds.size > 0) {
    body.sections = [...sectionIds].join(',');
    body.sections_url = window.location.pathname;
  }

  let updatedCart;
  try {
    const response = await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
    updatedCart = await response.json();
  } catch (error) {
    console.warn('[cart-strip-products] Could not update cart:', error);
    return;
  }

  const sections = /** @type {Record<string, string>} */ (updatedCart?.sections ?? {});
  const itemCount = updatedCart?.item_count ?? 0;

  // If keyboard focus is inside a line we're about to remove, remember it: the
  // morph deletes that node, which would otherwise silently drop focus to
  // <body> (WCAG 2.4.3). This is possible on a fresh /cart or open-drawer load
  // where the checkout-only line is server-rendered and briefly focusable.
  const removedKeySet = new Set(removedKeys);
  const focusRow = document.activeElement instanceof HTMLElement ? document.activeElement.closest('[data-key]') : null;
  const focusWasInRemovedLine = focusRow instanceof HTMLElement && removedKeySet.has(focusRow.dataset.key ?? '');

  // Update the UI deterministically by morphing each cart section with the fresh
  // HTML we already fetched. We do this directly rather than dispatching a cart
  // event for the components to handle, because the cart drawer hydrates lazily
  // and a `pageshow`-time dispatch races ahead of its listener and is missed —
  // which left the stripped product visible in the mini-cart until a reload.
  for (const el of cartComponents) {
    const id = el.getAttribute('data-section-id');
    const html = id ? sections[id] : null;
    if (!id || !html) continue;
    const isDrawer = el.hasAttribute('data-drawer') || el.closest('cart-drawer-component') != null;
    try {
      await morphSection(id, html, { mode: isDrawer ? 'hydration' : 'full' });
    } catch (error) {
      console.warn('[cart-strip-products] Could not morph section', id, error);
    }
  }

  // Restore focus if the morph dropped it to <body>.
  if (focusWasInRemovedLine && document.activeElement === document.body) {
    const fallback = document.querySelector(
      'cart-items-component .cart-items__remove:not([hidden]), cart-items-component .cart-items__empty-button, cart-drawer-component .theme-drawer__close-button'
    );
    if (fallback instanceof HTMLElement) fallback.focus();
  }

  // Update the header count bubble directly (same reason as the morph above).
  const cartIcon = /** @type {{ renderCartBubble?: (count: number, animate?: boolean) => void } | null} */ (
    document.querySelector('cart-icon')
  );
  cartIcon?.renderCartBubble?.(itemCount, false);

  announceRemoval(removedKeys.length);
}

/**
 * Announces the removal to assistive tech via a polite live region. The change
 * is not user-initiated, so without this a screen-reader user gets no signal
 * that the cart contents and count changed.
 * @param {number} count - Number of lines removed.
 * @returns {void}
 */
function announceRemoval(count) {
  const translations = /** @type {Record<string, string>} */ (Theme?.translations ?? {});
  const template = count === 1 ? translations.items_removed_from_cart_one : translations.items_removed_from_cart_other;
  if (!template) return;

  // Reuse the theme's existing cart-count live region (rendered by
  // `snippets/header-actions.liquid`) rather than adding a second status region
  // to the page. Fall back to creating one only if the header isn't present.
  let region = document.querySelector('[data-testid="cart-count-live-region"]');
  if (!region) {
    region = document.createElement('div');
    region.setAttribute('role', 'status');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
  }
  region.textContent = template.replace(/\{\{\s*count\s*\}\}/g, String(count));
}

let running = false;

/**
 * Runs the strip once at a time, ignoring overlapping triggers.
 * @returns {Promise<void>}
 */
async function run() {
  if (running) return;
  running = true;
  try {
    await stripCheckoutOnlyProducts();
  } finally {
    running = false;
  }
}

// `pageshow` fires on the initial load (event.persisted === false) AND when the
// page is restored from the back-forward cache (event.persisted === true) — e.g.
// the shopper taps "back" from checkout. Module scripts do NOT re-execute on a
// bfcache restore, so a plain top-level call would miss that first return and
// leave the checkout-only product visible in the mini-cart. Listening to
// `pageshow` covers both cases.
window.addEventListener('pageshow', run);
